import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useScrollTrigger,
  Collapse,
  Fade,
  Divider,
} from '@mui/material';
import { AccountCircle, ExitToApp } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext.js';
import { getUserCourses, getCourses } from '../services/api.js';
import LogoBranca from '../assets/LogoBranca.png';

const textButtonSx = {
  color: '#f5f5f5',
  fontWeight: 700,
  textTransform: 'none',
  px: 2,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
};

const loginButtonSx = {
  ml: 1,
  color: '#fff',
  borderColor: '#fff',
  textTransform: 'none',
  px: 2.5,
  '&:hover': {
    borderColor: '#a8d35e',
    color: '#a8d35e',
    backgroundColor: 'rgba(168, 211, 94, 0.1)',
  },
};

const NavBar = () => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated: authenticated, userRole, logout } = useAuth();

  const isLandingPage =
    location.pathname === '/' || location.pathname === '/cursos';

  const hasScrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  const isSolid = !isLandingPage || hasScrolled;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const role = useMemo(() => {
    if (!authenticated) return 'guest';
    if (userRole) return userRole.replace('ROLE_', '');
    return 'MEM';
  }, [authenticated, userRole]);

  const menuConfig = useMemo(
    () => ({
      guest: [
        { label: 'Home', to: '/' },
        {
          label: 'Contato',
          to: 'https://4controlgroup.com/contato/',
          external: true,
        },
      ],
      ADM: [
        { label: 'Dashboard Admin', to: '/dashboard' },
        { label: 'Monitorar Turmas', to: '/monitor-dashboard' },
        { label: 'Conteúdo do Curso', to: '__curso_viewer__', isCourseViewer: true },
        {
          label: 'Contato',
          to: 'https://4controlgroup.com/contato/',
          external: true,
        },
      ],
      MON: [
        { label: 'Cadastrar Alunos', to: '/users' },
        { label: 'Monitorar Turmas', to: '/monitor-dashboard' },
        { label: 'Conteúdo do Curso', to: '__curso_viewer__', isCourseViewer: true },
        {
          label: 'Contato',
          to: 'https://4controlgroup.com/contato/',
          external: true,
        },
      ],
      MBR: [
        { label: 'Início', to: '#inicio', isScroll: true },
        { label: 'Turmas', to: '#proximas-turmas', isScroll: true, dependsOnEnrollment: true },
        { label: 'Sobre o curso', to: '#sobre-curso', isScroll: true, isSobreCurso: true },
        { label: 'Ajuda', to: '#ajuda', isScroll: true },
      ],
      PRO: [
        { label: 'Conteúdo do Curso', to: '__curso_viewer__', isCourseViewer: true },
        {
          label: 'Contato',
          to: 'https://4controlgroup.com/contato/',
          external: true,
        },
      ],
      COR: [
        { label: 'Conteúdo do Curso', to: '__curso_viewer__', isCourseViewer: true },
        {
          label: 'Contato',
          to: 'https://4controlgroup.com/contato/',
          external: true,
        },
      ],
    }),
    []
  );

  const homeRoute = useMemo(() => {
    if (role === 'ADM') return '/dashboard';
    if (role === 'MON') return '/monitor-dashboard';
    if (role === 'MBR') return '/cursos';
    return '/';
  }, [role]);

  const handleMenuOpen = useCallback(e => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    handleMenuClose();
    setMobileOpen(false);
  }, [handleMenuClose, logout]);

  const [isEnrolled, setIsEnrolled] = useState(false);
  const [cursoVersaoId, setCursoVersaoId] = useState(null);

  useEffect(() => {
    if (authenticated && (role === 'MBR' || role === 'MEM')) {
      getUserCourses()
        .then(res => {
          const turmas = Array.isArray(res)
            ? res
            : res.cursos_usuario || res.data || [];
          setIsEnrolled(turmas.length > 0);
        })
        .catch(err => console.error('Erro ao verificar matrícula', err));
    }
  }, [authenticated, role]);

  // Fetch course version ID for staff roles
  useEffect(() => {
    if (authenticated && ['ADM', 'MON', 'COR', 'PRO'].includes(role)) {
      getCourses(1, 1)
        .then(res => {
          const courses = res?.items || res?.cursos || (Array.isArray(res) ? res : []);
          if (courses.length > 0) {
            const firstCourse = courses[0];
            const versaoId = firstCourse.versoes?.[0]?.id_curso_versao || firstCourse.id_curso;
            setCursoVersaoId(versaoId);
          }
        })
        .catch(() => { });
    }
  }, [authenticated, role]);

  const currentNavConfig = useMemo(() => {
    const config = menuConfig[role] || [];
    return config
      .map(item => {
        if (item.dependsOnEnrollment) {
          return {
            ...item,
            label: isEnrolled ? 'Meus cursos' : 'Turmas',
            to: isEnrolled ? '#meus-cursos' : '#proximas-turmas',
          };
        }
        if (item.isSobreCurso) {
          return {
            ...item,
            label: isEnrolled ? 'Turmas' : 'Sobre o curso',
            to: isEnrolled ? '#proximas-turmas' : '#sobre-curso',
          };
        }
        if (item.isCourseViewer) {
          if (!cursoVersaoId) return null;
          return {
            ...item,
            to: `/curso/versao/${cursoVersaoId}/visualizar`,
            isCourseViewer: false,
          };
        }
        return item;
      })
      .filter(Boolean);
  }, [menuConfig, role, isEnrolled, cursoVersaoId]);

  const handleScrollTo = (id) => {
    handleMenuClose();
    setMobileOpen(false);

    // If we're not on the home page, redirect to the home page with the hash
    if (location.pathname !== '/cursos' && location.pathname !== '/') {
      window.location.href = `/cursos${id}`;
      return;
    }

    if (id === '#inicio') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Attempt to scroll immediately, or set up a small timeout if the page is loading
    const element = document.getElementById(id.replace('#', ''));
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.location.hash = id;
    }
  };

  const navItems = currentNavConfig.map(({ label, to, external, isScroll }) =>
    external ? (
      <Button
        key={to}
        component='a'
        href={to}
        target='_blank'
        rel='noopener noreferrer'
        sx={textButtonSx}
      >
        {label}
      </Button>
    ) : isScroll ? (
      <Button
        key={to}
        onClick={() => handleScrollTo(to)}
        sx={textButtonSx}
      >
        {label}
      </Button>
    ) : (
      <Button key={to} component={RouterLink} to={to} sx={textButtonSx}>
        {label}
      </Button>
    )
  );

  return (
    <AppBar
      position='fixed'
      elevation={isSolid ? 4 : 0}
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        backgroundColor: isSolid ? '#021b1f' : 'transparent',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      {/* ── Main toolbar ── */}
      <Toolbar disableGutters>
        {/* Original container class kept for desktop layout parity */}
        <div className='container mx-auto px-6 flex items-center justify-between w-full max-w-[1536px]'>
          {/* Logo – slight inset so it's not glued to edge on mobile */}
          <Box sx={{ pl: 0 }}>
            <RouterLink
              to={homeRoute}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Box
                component='img'
                src={LogoBranca}
                alt='Develo PPP'
                sx={{ height: 50, display: 'block' }}
              />
            </RouterLink>
          </Box>

          {/* ── Desktop nav (unchanged from original) ── */}
          <Box
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
          >
            {navItems}

            {authenticated ? (
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{ color: '#f5f5f5', ml: 0.5 }}
                >
                  <AccountCircle />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    component={RouterLink}
                    to='/minha-area'
                    onClick={handleMenuClose}
                  >
                    <AccountCircle sx={{ mr: 1 }} />
                    Minha área
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ExitToApp sx={{ mr: 1 }} />
                    Sair
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                component={RouterLink}
                to='/login'
                variant='outlined'
                sx={loginButtonSx}
              >
                Entrar
              </Button>
            )}
          </Box>

          {/* ── Mobile right side ── */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              pr: 0,
            }}
          >
            {authenticated ? (
              /* Logged-in: hamburger icon */
              <IconButton
                edge='end'
                color='inherit'
                aria-label='menu'
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              /* Guest: inline text buttons – no hamburger */
              <>
                {(menuConfig['guest'] || []).map(({ label, to, external }) =>
                  external ? (
                    <Button
                      key={to}
                      component='a'
                      href={to}
                      target='_blank'
                      rel='noopener noreferrer'
                      size='small'
                      sx={{
                        color: '#f5f5f5',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 1,
                        fontSize: '0.82rem',
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                        },
                      }}
                    >
                      {label}
                    </Button>
                  ) : (
                    <Button
                      key={to}
                      component={RouterLink}
                      to={to}
                      size='small'
                      sx={{
                        color: '#f5f5f5',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 1,
                        fontSize: '0.82rem',
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                        },
                      }}
                    >
                      {label}
                    </Button>
                  )
                )}
                <Button
                  component={RouterLink}
                  to='/login'
                  variant='outlined'
                  size='small'
                  sx={{
                    ...loginButtonSx,
                    ml: 0.5,
                    px: 1.5,
                    fontSize: '0.82rem',
                  }}
                >
                  Entrar
                </Button>
              </>
            )}
          </Box>
        </div>
      </Toolbar>

      {/* ── Mobile dropdown (logged-in only) ── */}
      {authenticated && (
        <>
          {/* Blurred backdrop – starts below the AppBar so navbar is never blurred */}
          <Fade in={mobileOpen} timeout={250}>
            <Box
              onClick={() => setMobileOpen(false)}
              sx={{
                display: { xs: 'block', md: 'none' },
                position: 'fixed',
                top: { xs: '56px', sm: '64px' },
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme => theme.zIndex.drawer,
                backdropFilter: 'blur(5px)',
                backgroundColor: 'rgba(0,0,0,0.45)',
                pointerEvents: mobileOpen ? 'auto' : 'none',
              }}
            />
          </Fade>

          <Collapse in={mobileOpen} timeout='auto' unmountOnExit>
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                backgroundColor: '#021b1f',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                px: 2,
                pb: 2,
                // Ensure it sits above the backdrop
                position: 'relative',
                zIndex: theme => theme.zIndex.drawer + 1,
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}
              >
                {(menuConfig[role] || []).map(({ label, to, external }) =>
                  external ? (
                    <Button
                      key={to}
                      component='a'
                      href={to}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        ...textButtonSx,
                        justifyContent: 'flex-start',
                        width: '100%',
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      {label}
                    </Button>
                  ) : (
                    <Button
                      key={to}
                      component={RouterLink}
                      to={to}
                      sx={{
                        ...textButtonSx,
                        justifyContent: 'flex-start',
                        width: '100%',
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      {label}
                    </Button>
                  )
                )}

                <Divider
                  sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }}
                />

                <Button
                  component={RouterLink}
                  to='/minha-area'
                  sx={{
                    ...textButtonSx,
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                  onClick={() => setMobileOpen(false)}
                >
                  <AccountCircle sx={{ mr: 1, fontSize: 20 }} />
                  Minha área
                </Button>

                <Button
                  onClick={handleLogout}
                  sx={{
                    ...textButtonSx,
                    justifyContent: 'flex-start',
                    width: '100%',
                    color: '#f87171',
                    '&:hover': { backgroundColor: 'rgba(248,113,113,0.08)' },
                  }}
                >
                  <ExitToApp sx={{ mr: 1, fontSize: 20 }} />
                  Sair
                </Button>
              </Box>
            </Box>
          </Collapse>
        </>
      )}
    </AppBar>
  );
};

export default NavBar;
